import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, notification } from "antd";
// FONT AWESOME LIBRYARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { usePostMonitoringCommandMutation } from "../../../redux/monitoring/monitoringAPI";
import { updateSendNewMessage } from "../../../redux/monitoring/monitoringSlice";
// AUTHENTICATION
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { RootState } from "../../../app/store";
import { ControlPannelCommandButtonInterface } from "../../../interfaces/EdgeManagement.interface";

function ControlPannelCommandButton({
	name,
	carService,
	defaultState,
	validStates,
}: ControlPannelCommandButtonInterface) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [lastCommandName, setLastCommandName] = useState<string>("");
	const [actionOnOff, setActionOnOff] = useState<string>("OFF");
	const [checkServiceMode, setCheckServiceMode] = useState<boolean>(false);
	const [showIsLoading, setShowIsLoading] = useState<boolean>(false);
	const [stopTimer, setStopTimer] = useState<boolean>(false);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { liftSelected, layoutSelected, socketConnection } = useSelector(
		(state: RootState) => state.monitoring
	);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		postCommand,
		{ isLoading: isLoadingPostCommand, isSuccess, isError, error },
	] = usePostMonitoringCommandMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onClickCommand = async (command: string) => {
		const token = await GETJwtToken();
		const BODY = {
			token,
			body: {
				lift_id: liftSelected.id,
				short_msg: command,
			},
			serverId: layoutSelected.idServer,
		};
		postCommand(BODY);
	};

	const onClickCommandEmulator = (shortMessage: string) => {
		if (socketConnection === "EMULATOR") {
			dispatch(
				updateSendNewMessage({
					bank_id: layoutSelected.id,
					lift_id: liftSelected.id,
					bankName: layoutSelected.name,
					liftName: liftSelected.name,
					msg: {
						type: "CARSERVICE",
						message: "",
						short_msg: shortMessage,
					},
				})
			);
		}
	};

	const sendSuccessMessage = () => {
		notification.success({
			message: "Success",
			description: `${lastCommandName} ${actionOnOff}: Successfully Executed`,
			placement: "top",
			className: "generalStyles__notificationSuccess",
		});
	};

	const sendErrorMessage = () => {
		notification.error({
			message: "Error",
			description: `${lastCommandName} ${actionOnOff} Failed: No Response from Controller`,
			placement: "top",
			className: "generalStyles__notificationError",
		});
	};

	const checkIncommingServiceMode = () => {
		setStopTimer(true);
		setShowIsLoading(false);
		if (
			actionOnOff &&
			actionOnOff === "ON" &&
			validStates.includes(carService)
		) {
			sendSuccessMessage();
		} else if (
			actionOnOff &&
			actionOnOff === "OFF" &&
			(carService === "" || carService === "AUTO")
		) {
			sendSuccessMessage();
		} else {
			sendErrorMessage();
		}
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		if (isSuccess) {
			setTimeout(() => {
				setCheckServiceMode(true);
				setTimeout(() => {
					setCheckServiceMode(false);
				}, 1000);
				setShowIsLoading(false);
			}, 5000);
		} else {
			setShowIsLoading(true);
		}
	}, [isSuccess]);

	useEffect(() => {
		setShowIsLoading(false);
		const newError: any = error;
		if (isError) {
			notification.error({
				message: "Error",
				description:
					newError && newError.data && newError.data.message
						? `${newError.data.message} / ${lastCommandName}`
						: "",
				placement: "top",
				className: "generalStyles__notificationError",
			});
		}
	}, [isError]);

	useEffect(() => {
		if (checkServiceMode && !stopTimer) {
			checkIncommingServiceMode();
		}
	}, [checkServiceMode]);

	useEffect(() => {
		if (carService && carService === lastCommandName) {
			checkIncommingServiceMode();
		}
	}, [carService]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Button
			onClick={() => {
				setShowIsLoading(true);
				setStopTimer(false);
				onClickCommand(defaultState);
				setActionOnOff(validStates.includes(carService) ? "OFF" : "ON");
				setLastCommandName(defaultState);
				onClickCommandEmulator(
					validStates.includes(carService) ? "" : defaultState
				);
			}}
			className={
				validStates.includes(carService)
					? "buttonStyle__1 monitoring__advanceSettings__button"
					: "buttonStyle__11 monitoring__advanceSettings__button"
			}
			loading={
				(isLoadingPostCommand && lastCommandName === defaultState) ||
				showIsLoading
			}
			disabled={isLoadingPostCommand || showIsLoading}
			icon={
				validStates.includes(carService) ? (
					<FontAwesomeIcon className='generalStyles__mrFix' icon={faPowerOff} />
				) : (
					""
				)
			}
		>
			<span>
				{name} - {validStates.includes(carService) ? "OFF" : "ON"}
			</span>
		</Button>
	);
}

export default ControlPannelCommandButton;
